import { useState } from "react";
import { getStockInfo } from "../../api/salesApi";

/**
 * Props:
 * - items: [{ product_id: string, quantity: number, price_per_unit: number, unit?: 'bottle'|'case' }]
 * - setItems: (next) => void
 * - products: [{ id: string|number, name: string, sku?: string }]
 */
export default function ItemSelector({ items, setItems, products = [] }) {
  const [openIndex, setOpenIndex] = useState(null);
  const [queries, setQueries] = useState({});
  const [apiCache, setApiCache] = useState({}); // { [productId]: { derived_unit_price, base_price_per_case, case_pack_qty } }

  const addItem = () =>
    setItems([
      ...items,
      { product_id: "", quantity: 1, price_per_unit: 0, unit: "case" },
    ]);

  const updateItem = (index, field, value) => {
    setItems((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  const updateMany = (index, patch) => {
    setItems((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], ...patch };
      return copy;
    });
  };

  const removeItem = (index) => {
    const copy = items.filter((_, i) => i !== index);
    setItems(copy);
  };

  const setQuery = (index, val) => setQueries((q) => ({ ...q, [index]: val }));

  const filteredForRow = (index) => {
    const q = (queries[index] || "").trim().toLowerCase();
    if (!q) return products.slice(0, 50);
    return products
      .filter(
        (p) =>
          p?.name?.toLowerCase().includes(q) ||
          String(p?.sku || "")
            .toLowerCase()
            .includes(q) ||
          String(p?.id || "")
            .toLowerCase()
            .includes(q)
      )
      .slice(0, 50);
  };

  const getSelectedProduct = (product_id) =>
    products.find((p) => String(p.id) === String(product_id));

  const coerceNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const fetchAndCacheProduct = async (pid) => {
    if (apiCache[pid]) return apiCache[pid];
    const data = await getStockInfo(pid);
    const p = data?.product || {};
    const cached = {
      derived_unit_price: coerceNumber(p.derived_unit_price),
      base_price_per_case: coerceNumber(p.base_price_per_case),
      case_pack_qty: coerceNumber(p.case_pack_qty),
      name: p.name,
      size_label: p.size_label,
      unit_type: p.unit_type,
    };
    setApiCache((s) => ({ ...s, [pid]: cached }));
    return cached;
  };

  const selectProduct = async (rowIndex, product) => {
    setOpenIndex(null);
    // immediately reflect selection & default unit
    updateMany(rowIndex, { product_id: String(product.id), unit: "case" });
    try {
      const cached = await fetchAndCacheProduct(product.id);
      updateItem(rowIndex, "price_per_unit", cached.base_price_per_case);
    } catch {
      /* noop */
    }
  };

  const onUnitChange = async (rowIndex, nextUnit) => {
    const it = items[rowIndex];
    if (!it?.product_id) {
      updateItem(rowIndex, "unit", nextUnit);
      return;
    }
    try {
      const cached = await fetchAndCacheProduct(it.product_id);
      updateItem(rowIndex, "unit", nextUnit);
      updateItem(
        rowIndex,
        "price_per_unit",
        nextUnit === "case"
          ? cached.base_price_per_case
          : cached.derived_unit_price
      );
    } catch {
      updateItem(rowIndex, "unit", nextUnit);
    }
  };

  const resetPriceToDefault = async (rowIndex) => {
    const it = items[rowIndex];
    if (!it?.product_id) return;
    const unit = it.unit || "case";
    try {
      const cached = await fetchAndCacheProduct(it.product_id);
      updateItem(
        rowIndex,
        "price_per_unit",
        unit === "case" ? cached.base_price_per_case : cached.derived_unit_price
      );
    } catch {
      /* noop */
    }
  };

  const stepQty = (idx, delta) => {
    const next = Math.max(1, (items[idx]?.quantity || 1) + delta);
    updateItem(idx, "quantity", next);
  };

  const formatINR = (n) =>
    Number.isFinite(Number(n)) ? `₹${Number(n).toFixed(2)}` : "₹0.00";

  return (
    <div className="w-full">
      <div className="mx-auto max-w-6xl rounded-2xl border border-gray-200 bg-white/80 shadow-sm backdrop-blur-sm">
        {/* Header */}
        <div className="flex flex-col gap-1 border-b border-gray-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <h3 className="text-base font-semibold tracking-tight text-gray-900">
            Items
          </h3>
          <span className="text-xs font-medium text-gray-500">
            {items.length} selected
          </span>
        </div>

        {/* Items */}
        <div className="space-y-3 p-3 sm:p-4 md:p-6">
          {items.map((it, idx) => {
            const selected = getSelectedProduct(it.product_id);
            const list = filteredForRow(idx);
            const unit = it.unit || "case";
            const cached = apiCache[it.product_id];
            const lineTotal =
              coerceNumber(it.price_per_unit) * coerceNumber(it.quantity);

            return (
              <div
                key={idx}
                className="group rounded-xl border border-gray-200 bg-white px-3 py-3 shadow-sm transition-all hover:shadow-md sm:px-4"
              >
                {/* 
                  Responsive grid:
                  - Mobile: 1 column stacked (gap tight)
                  - sm: 6 cols
                  - md+: 12 cols
                */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-6 md:grid-cols-12">
                  {/* Product combobox */}
                  <div className="sm:col-span-6 md:col-span-4 relative z-[60] overflow-visible">
                    <label className="mb-1 block text-[11px] font-medium text-gray-600">
                      Product
                    </label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() =>
                          setOpenIndex(openIndex === idx ? null : idx)
                        }
                        className="flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-sm text-gray-900 transition focus:border-indigo-500 focus:outline-none focus:ring-4 focus:ring-indigo-100"
                        aria-haspopup="listbox"
                        aria-expanded={openIndex === idx}
                      >
                        <span className="truncate">
                          {selected ? (
                            <>
                              <span className="font-medium">
                                {selected.name}
                              </span>
                              {selected.sku && (
                                <span className="ml-2 text-xs text-gray-500">
                                  • {selected.sku}
                                </span>
                              )}
                              <span className="ml-2 text-xs text-gray-400">
                                (ID: {selected.id})
                              </span>
                            </>
                          ) : (
                            <span className="text-gray-400">
                              Select a product…
                            </span>
                          )}
                        </span>
                        <svg
                          className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${
                            openIndex === idx ? "rotate-180" : ""
                          }`}
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.25 8.29a.75.75 0 01-.02-1.08z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>

                      {/* Dropdown (full-width, mobile-safe) */}
                      {openIndex === idx && (
                        <div
                          className="absolute left-0 top-full mt-2 w-full rounded-xl border border-gray-200 bg-white shadow-xl z-[9999]"
                          role="listbox"
                          tabIndex={-1}
                        >
                          <div className="p-2">
                            <input
                              autoFocus
                              type="text"
                              placeholder="Search by name, SKU, or ID…"
                              value={queries[idx] || ""}
                              onChange={(e) => setQuery(idx, e.target.value)}
                              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
                            />
                          </div>

                          <div className="max-h-64 overflow-auto py-1">
                            {list.length === 0 ? (
                              <div className="px-3 py-6 text-center text-sm text-gray-500">
                                No products found
                              </div>
                            ) : (
                              list.map((p) => {
                                const isActive =
                                  String(p.id) === String(it.product_id);
                                return (
                                  <button
                                    key={p.id}
                                    type="button"
                                    onClick={() => selectProduct(idx, p)}
                                    className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm transition hover:bg-indigo-50 ${
                                      isActive ? "bg-indigo-50" : ""
                                    }`}
                                  >
                                    <div className="min-w-0">
                                      <div className="truncate font-medium text-gray-900">
                                        {p.name} - {p?.size_label || ""}
                                      </div>
                                      <div className="truncate text-xs text-gray-500">
                                        {p.brand ? `Brand: ${p.brand} ` : ""} -{" "}
                                        {p?.case_pack_qty || ""} Bottles -{" "}
                                      </div>
                                    </div>
                                    {isActive && (
                                      <svg
                                        className="ml-3 h-4 w-4 shrink-0 text-indigo-600"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M16.704 5.29a1 1 0 00-1.408-1.42L8 11.167 4.704 7.871A1 1 0 003.296 9.29l4 4a1 1 0 001.408 0l8-8z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                    )}
                                  </button>
                                );
                              })
                            )}
                          </div>

                          <div className="flex items-center justify-end gap-2 border-t border-gray-100 p-2">
                            <button
                              type="button"
                              onClick={() => setOpenIndex(null)}
                              className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50"
                            >
                              Close
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    {!selected && (
                      <p className="mt-1 text-[11px] text-amber-600">
                        Only predefined products can be selected.
                      </p>
                    )}
                    {cached?.case_pack_qty ? (
                      <p className="mt-1 text-[11px] text-gray-500">
                        <span className="font-medium">
                          {cached.case_pack_qty}
                        </span>{" "}
                        bottles per case
                        {cached.size_label ? ` • ${cached.size_label}` : ""}
                      </p>
                    ) : null}
                  </div>

                  {/* Unit selector */}
                  <div className="sm:col-span-3 md:col-span-2">
                    <label className="mb-1 block text-[11px] font-medium text-gray-600">
                      Unit
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => onUnitChange(idx, "bottle")}
                        className={`h-10 rounded-lg border text-sm font-medium transition focus:outline-none focus:ring-4 focus:ring-indigo-100 ${
                          (it.unit || "bottle") === "bottle"
                            ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                            : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        Bottle
                      </button>
                      <button
                        type="button"
                        onClick={() => onUnitChange(idx, "case")}
                        className={`h-10 rounded-lg border text-sm font-medium transition focus:outline-none focus:ring-4 focus:ring-indigo-100 ${
                          (it.unit || "bottle") === "case"
                            ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                            : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        Case
                      </button>
                    </div>
                  </div>

                  {/* Quantity */}
                  <div className="sm:col-span-3 md:col-span-2">
                    <label className="mb-1 block text-[11px] font-medium text-gray-600">
                      Quantity
                    </label>
                    <div className="flex items-center rounded-lg border border-gray-300 bg-white">
                      <button
                        type="button"
                        className="h-10 w-10 select-none rounded-l-lg text-xl text-gray-600 transition hover:bg-gray-50 focus:outline-none focus:ring-4 focus:ring-indigo-100"
                        onClick={() => stepQty(idx, -1)}
                        aria-label="Decrease"
                      >
                        −
                      </button>
                      <input
                        type="number"
                        min="1"
                        className="h-10 w-full appearance-none border-0 text-center text-sm text-gray-900 outline-none focus:ring-0"
                        value={it.quantity}
                        onChange={(e) =>
                          updateItem(
                            idx,
                            "quantity",
                            Math.max(1, +e.target.value || 1)
                          )
                        }
                      />
                      <button
                        type="button"
                        className="h-10 w-10 select-none rounded-r-lg text-xl text-gray-600 transition hover:bg-gray-50 focus:outline-none focus:ring-4 focus:ring-indigo-100"
                        onClick={() => stepQty(idx, +1)}
                        aria-label="Increase"
                      >
                        +
                      </button>
                    </div>
                    <p className="mt-1 text-[11px] text-gray-500">
                      Qty in {it.unit || "case"}s
                    </p>
                  </div>

                  {/* Price per unit (editable, with reset) */}
                  <div className="sm:col-span-3 md:col-span-2">
                    <label className="mb-1 block text-[11px] font-medium text-gray-600">
                      Price /{" "}
                      {(it.unit || "case") === "case" ? "Case" : "Bottle"} (this
                      sale)
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="flex h-10 w-full items-center rounded-lg border border-gray-300 bg-white px-2">
                        <span className="px-2 text-sm text-gray-500">₹</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="h-10 w-full border-0 bg-transparent text-sm text-gray-900 outline-none focus:ring-0"
                          value={it.price_per_unit?.toFixed(2) ?? 0}
                          onChange={(e) =>
                            updateItem(
                              idx,
                              "price_per_unit",
                              coerceNumber(e.target.value)
                            )
                          }
                        />
                      </div>
                      <button
                        type="button"
                        className="h-10 shrink-0 rounded-lg border border-gray-300 px-3 text-sm font-medium text-gray-700 transition hover:bg-gray-50 focus:outline-none focus:ring-4 focus:ring-indigo-100"
                        onClick={() => resetPriceToDefault(idx)}
                        title="Reset to default price from product"
                      >
                        Reset
                      </button>
                    </div>
                    {selected && cached ? (
                      <p className="mt-1 text-[11px] text-gray-500">
                        Default:{" "}
                        {(it.unit || "case") === "case"
                          ? formatINR(cached.base_price_per_case)
                          : formatINR(cached.derived_unit_price)}
                      </p>
                    ) : null}
                  </div>

                  {/* Line Total */}
                  <div className="sm:col-span-3 md:col-span-1">
                    <label className="mb-1 block text-[11px] font-medium text-gray-600">
                      Line Total
                    </label>
                    <div className="flex h-10 items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm text-gray-900">
                      <span className="tabular-nums">
                        {formatINR(lineTotal)}
                      </span>
                    </div>
                  </div>

                  {/* Remove */}
                  <div className="sm:col-span-3 md:col-span-1 flex items-center justify-end">
                    <button
                      type="button"
                      title="Remove item"
                      className="inline-flex h-10 items-center justify-center rounded-lg border border-transparent px-3 text-sm font-semibold text-red-600 transition hover:bg-red-50 focus:outline-none focus:ring-4 focus:ring-red-100 active:scale-95"
                      onClick={() => removeItem(idx)}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Add Item */}
          <div className="pt-1">
            <button
              type="button"
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 focus:outline-none focus:ring-4 focus:ring-emerald-200 active:scale-95 sm:w-auto"
              onClick={addItem}
            >
              <span className="text-base leading-none">＋</span> Add Item
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
